"use client";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { Upload, Trash2, Download, RefreshCcw, FileText, BookOpen, DatabaseZap, Share2, Building2, Eye } from "lucide-react";
import { Row, Col } from "../Common/Row";
import { DocumentStatusChip } from "./DocumentStatusChip";
import { PageHeader } from "../Common/PageHeader";
import { EmptyState } from "../Common/EmptyState";
import { CardSkeletonList } from "../Common/CardSkeleton";
import { WorkspaceCard } from "../Common/WorkspaceCard";
import { ConfirmDialog } from "../Common/ConfirmDialog";
import { useAuthedApi } from "../../api/useAuthedApi";
import type {
  DocumentRow,
  DocumentScope,
  DocumentsListing,
  GroupedDocuments,
} from "../../api/documentsApi";
import { docDeptIds, parseDocDeptId } from "../../api/documentsApi";
import { RestError } from "../../api/restClient";
import { type AuthUser, hasPermission } from "../../auth/useAuth";
import type { Department } from "../../api/departmentsApi";
import { parseMineDeptId } from "../../api/departmentsApi";
import { ShareDocumentDialog } from "./ShareDocumentDialog";
import { PromoteVisibilityDialog } from "./PromoteVisibilityDialog";
import { usePortalScope } from "@/analytics/PortalScope";
import { useSearchParams } from "react-router-dom";
import type { ConversationDocument } from "../../api/conversationKnowledgeApi";
import { ConversationKnowledgeView } from "./ConversationKnowledgeView";

type TierKey = keyof GroupedDocuments;
const TIERS: TierKey[] = ["user", "department", "company", "platform"];
const TIER_LABEL: Record<TierKey, string> = {
  user: "Personal",
  department: "Department",
  company: "Company",
  platform: "Platform",
};
const UPLOADABLE_TIERS: Record<TierKey, boolean> = {
  user: true,
  department: true,
  company: true,
  // Platform documents are uploadable only by platform admins — the button is
  // gated at runtime (see uploadGate). Listed platform docs are writable only
  // where the server's per-document `can_write` says so (platform admins).
  platform: true,
};

/** UX-only tier gating (the backend re-enforces on POST): mirrors
 *  SkillsPage.createGate — company uploads need company:manage; department
 *  uploads need at least one manageable department; platform uploads need
 *  platform-admin; personal always allowed. */
function uploadGate(
  tier: TierKey,
  canManageCompany: boolean,
  hasDepartments: boolean,
  isPlatformAdmin: boolean,
): { enabled: boolean; reason?: string } {
  if (!UPLOADABLE_TIERS[tier])
    return { enabled: false, reason: `${TIER_LABEL[tier]} documents are read-only` };
  if (tier === "company" && !canManageCompany)
    return { enabled: false, reason: "Only a company admin can upload company documents" };
  if (tier === "department" && !hasDepartments)
    return { enabled: false, reason: "You don't belong to any departments yet" };
  // Deliberately NOT company:manage: a platform document is readable by every
  // tenant, so a company admin must not be able to publish one.
  if (tier === "platform" && !isPlatformAdmin)
    return { enabled: false, reason: "Only a platform admin can upload platform documents" };
  return { enabled: true };
}

function formatBytes(n: number | null | undefined): string {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Whether "Add to / Re-index in knowledge base" is offered. Not while it
 *  runs, and not for an image that ended as a stored photo or as nothing to
 *  search: ingestion would classify it the same way again, a paid round trip
 *  to the same status. */
function promotable(status: string): boolean {
  return status !== "processing" && status !== "pending_media" && status !== "skipped";
}

export function KnowledgePage() {
  const { documents, auth, departments, conversationKnowledge } = useAuthedApi();
  // The department menu is portaled out of the header, so it names its path (D7).
  const deptMenuScope = usePortalScope("header-dept");
  const [data, setData] = useState<DocumentsListing | null>(null);
  const [tab, setTab] = useState<TierKey>("user");
  const [manageableDepts, setManageableDepts] = useState<Department[]>([]);
  // Every department in the company — the name source for document rows,
  // including departments the caller can't upload to but can read docs from.
  const [allDepts, setAllDepts] = useState<Department[]>([]);
  const [isDeptEditor, setIsDeptEditor] = useState(false);
  // Doubles as the department-tier upload target (uploads require a dept_id)
  // and as the list filter on the Department tab; "" means "all departments".
  const [deptId, setDeptId] = useState<number | "">("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  // Set when an upload comes back status: "duplicate_personal" (the caller
  // already has this exact file in their Personal library) — drives a
  // dismissible info Alert with a "Promote…" action targeting that doc.
  const [duplicatePersonalPrompt, setDuplicatePersonalPrompt] = useState<{
    documentId: number;
    filename: string;
  } | null>(null);
  const [duplicatePromoteOpen, setDuplicatePromoteOpen] = useState(false);
  // Conversations stored as knowledge (spec §7.3). null = the feature is off
  // (every route 404s) or not loaded yet, so the view is not offered.
  const [conversations, setConversations] = useState<ConversationDocument[] | null>(null);
  // The "User conversations" view and the conversation opened in it ride the
  // URL (?view=conversations&conversation=<id>), so the chat's "View it"
  // link and the back button land on the right place.
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationsView = searchParams.get("view") === "conversations";
  const openConversationId = Number(searchParams.get("conversation")) || null;
  const showConversations = conversationsView && conversations !== null;
  const setConversationView = useCallback(
    (on: boolean, conversationId: number | null = null) => {
      const next = new URLSearchParams();
      if (on) next.set("view", "conversations");
      if (on && conversationId != null) next.set("conversation", String(conversationId));
      setSearchParams(next);
    },
    [setSearchParams],
  );

  // Load the current user so we can gate the Share affordance on company-admin
  // (cosmetic — the backend re-derives per-document manage rights via a live
  // FGA check and is the real enforcement boundary; owners/editors of a doc
  // who aren't company-admins still get a working Share button per-row via
  // that server-side check, this just avoids flashing it for everyone).
  // Routed through `useAuthedApi` so the call works under BOTH the cookie
  // SPA flow and the embed bearer flow.
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      auth.me().then(
        (u) => { if (!cancelled) setUser(u); },
        () => { if (!cancelled) setUser(null); },
      );
    };
    refresh();
    // Re-fetch on tab focus so out-of-band permission changes take effect.
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [auth]);
  const canManageSharing = hasPermission(user, "company:manage");

  // Load departments the caller may upload to: admins see every company
  // department; everyone else sees only /mine memberships (names from list()).
  useEffect(() => {
    if (!departments) return;
    let cancelled = false;
    (async () => {
      let all: Department[] = [];
      try {
        all = await departments.list();
      } catch {
        all = [];
      }
      if (!cancelled) setAllDepts(all);
      try {
        const mine = await departments.listMine();
        if (cancelled) return;
        const mineIds = mine.department_ids
          .map((oid) => parseMineDeptId(oid))
          .filter((id): id is number => id != null);
        setIsDeptEditor(mineIds.length > 0);
        if (all.length > 0) {
          const mineSet = new Set(mineIds);
          setManageableDepts(
            hasPermission(user, "company:manage")
              ? all
              : all.filter((d) => mineSet.has(d.id)),
          );
        } else {
          setManageableDepts(
            mineIds.map((id) => ({ id, name: `Department ${id}`, parent_id: null })),
          );
        }
      } catch {
        if (!cancelled) {
          setIsDeptEditor(false);
          setManageableDepts(hasPermission(user, "company:manage") ? all : []);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [departments, user]);

  const canManageCompany = hasPermission(user, "company:manage");
  const isPlatformAdmin = Boolean(user?.is_platform_admin);
  // Deliberately permissive (backend re-enforces on POST) — same rationale
  // as SkillsPage.hasDepartments.
  const hasDepartments =
    canManageCompany || isDeptEditor || manageableDepts.length > 0;
  const gate = uploadGate(tab, canManageCompany, hasDepartments, isPlatformAdmin);
  const needsDept = tab === "department";
  // The picker doubles as a filter, so it also lists departments the caller can
  // only READ from. An upload targets whatever is picked, so a read-only pick
  // must not enable Upload (the backend would 403 it anyway). An empty
  // manageable list means both department lookups failed — fall back to the
  // coarse gate rather than locking out a legitimate editor.
  const canUploadToPickedDept =
    typeof deptId === "number" &&
    (manageableDepts.length === 0 ||
      manageableDepts.some((d) => d.id === deptId));
  const deptOk = !needsDept || canUploadToPickedDept;
  const canUpload = gate.enabled && deptOk;

  const refresh = useCallback(async () => {
    if (!documents) return;
    setRefreshing(true);
    setLoadError(null);
    try {
      const d = await documents.list();
      setData(d);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  }, [documents]);

  const refreshConversations = useCallback(async () => {
    if (!conversationKnowledge) return;
    try {
      // null = the feature is off: the view is not offered.
      setConversations(await conversationKnowledge.list());
    } catch (e) {
      // Fail safe: the page renders its document tiers either way.
      console.warn("conversation knowledge list failed", e);
    }
  }, [conversationKnowledge]);

  useEffect(() => {
    void refreshConversations();
  }, [refreshConversations]);

  // Keep the latest data in a ref so the poll interval can read it WITHOUT
  // being in the effect's dependency array. (Depending on `data` there caused
  // a fetch→setData→re-run→fetch loop — refresh() always returns a fresh
  // object, so the effect re-fired every tick and the page flickered.)
  const dataRef = useRef(data);
  dataRef.current = data;

  // Initial load: mount-once.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll ONLY while something is processing, so the UI reflects ingest results
  // without a manual reload — and stops once everything is settled. The
  // interval is set up once; it reads current state via dataRef.
  useEffect(() => {
    const id = setInterval(() => {
      const current = dataRef.current;
      const anyProcessing = current
        ? TIERS.some((t) => current[t].some((d) => d.status === "processing"))
        : false;
      if (anyProcessing) refresh();
    }, 4000);
    return () => clearInterval(id);
  }, [refresh]);

  const deptNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const d of [...allDepts, ...manageableDepts]) m.set(d.id, d.name);
    return m;
  }, [allDepts, manageableDepts]);

  const deptName = useCallback(
    (id: number) => deptNameById.get(id) ?? `Department ${id}`,
    [deptNameById],
  );

  const docDeptName = useCallback(
    (doc: DocumentRow) => {
      const id = parseDocDeptId(doc.dept_id);
      return id != null ? deptName(id) : null;
    },
    [deptName],
  );

  // Filter options cover departments the caller may upload to PLUS any
  // department that owns or was shared a visible document — a plain member can read
  // their department's docs without being able to write there, and they still
  // need to be able to narrow the list down.
  const deptOptions = useMemo(() => {
    const byId = new Map<number, string>();
    for (const d of manageableDepts) byId.set(d.id, d.name);
    for (const doc of data?.department ?? []) {
      for (const id of docDeptIds(doc)) {
        if (!byId.has(id)) byId.set(id, deptName(id));
      }
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [manageableDepts, data, deptName]);

  const visible = useMemo(() => {
    const rows = data?.[tab] ?? [];
    if (tab !== "department" || typeof deptId !== "number") return rows;
    // A document shared with the picked department belongs here too, not
    // only the ones it owns.
    return rows.filter((d) => docDeptIds(d).includes(deptId));
  }, [data, tab, deptId]);
  const counts = useMemo(() => {
    const out: Record<TierKey, number> = {
      user: 0,
      department: 0,
      company: 0,
      platform: 0,
    };
    if (data) for (const t of TIERS) out[t] = data[t].length;
    return out;
  }, [data]);

  const onPickFile = () => fileRef.current?.click();

  const onUploaded = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!documents) return;
    const file = e.target.files?.[0];
    // Clear the input so re-uploading the same filename retriggers onChange.
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      // Explicit knowledge intent → the backend chunks + embeds + indexes the
      // file so it becomes searchable (status → ready). Matches the backend
      // default but we send it explicitly so the contract is unambiguous.
      // Department-tier uploads must carry the picked dept_id (backend 400s
      // without it); other tiers never send one.
      const result = await documents.upload({
        file,
        tier: tab as DocumentScope,
        intent: "knowledge",
        ...(tab === "department" && typeof deptId === "number"
          ? { dept_id: deptId }
          : {}),
      });
      // "duplicate_personal": the caller already has their own copy of this
      // exact file. Still refresh (the list needs to reflect reality either
      // way) but also surface a prompt so they can promote the existing doc's
      // visibility instead of ending up with a silent no-op-feeling upload.
      if (result.status === "duplicate_personal") {
        setDuplicatePersonalPrompt({ documentId: result.document_id, filename: file.name });
      }
      await refresh();
    } catch (err) {
      const msg =
        err instanceof RestError
          ? err.detail
          : err instanceof Error
          ? err.message
          : String(err);
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Knowledge"
        subtitle="Documents the assistant can search via tools. Scoped by tier — personal docs are private to you."
        actions={
          <div data-ph-scope="header" style={{ display: "contents" }}>
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={() => {
                    void refresh();
                    void refreshConversations();
                  }}
                  disabled={refreshing} aria-label="refresh" data-ph="refresh">
                  <RefreshCcw size={16} />
                </IconButton>
              </span>
            </Tooltip>
            {!showConversations && needsDept && deptOptions.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 180 }} data-ph-scope="dept">
                <InputLabel id="knowledge-dept-label">Department</InputLabel>
                <Select
                  data-ph="open"
                  MenuProps={{ slotProps: { list: deptMenuScope } }}
                  labelId="knowledge-dept-label"
                  label="Department"
                  value={deptId === "" ? "" : String(deptId)}
                  onChange={(e) =>
                    setDeptId(e.target.value === "" ? "" : Number(e.target.value))
                  }
                >
                  <MenuItem value="" data-ph="select">All departments</MenuItem>
                  {deptOptions.map((d) => (
                    <MenuItem key={d.id} value={String(d.id)} data-ph="select">
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!showConversations && (
              <Tooltip
                title={
                  canUpload
                    ? ""
                    : gate.reason ??
                      (!needsDept
                        ? ""
                        : typeof deptId === "number"
                        ? `You can't upload to ${deptName(deptId)}`
                        : "Pick a department first")
                }
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Upload size={16} />}
                    onClick={onPickFile}
                    disabled={uploading || !canUpload}
                    data-ph="upload"
                  >
                    {uploading ? "Uploading…" : "Upload"}
                  </Button>
                </span>
              </Tooltip>
            )}
            <input
              ref={fileRef}
              type="file"
              hidden
              onChange={onUploaded}
              aria-label="upload file"
            />
          </div>
        }
      />

      <Tabs
        value={showConversations ? "conversations" : tab}
        onChange={(_, v) => {
          if (v === "conversations") {
            setConversationView(true);
            return;
          }
          if (conversationsView) setConversationView(false);
          setTab(v as TierKey);
        }}
        data-ph-scope="tiers"
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        {TIERS.map((t) => (
          <Tab
            key={t}
            value={t}
            data-ph="select"
            label={
              <Row gap={1} align="center">
                {TIER_LABEL[t]}
                <Chip
                  size="small"
                  label={counts[t]}
                  sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: 11 } }}
                />
              </Row>
            }
          />
        ))}
        {conversations !== null && (
          <Tab
            value="conversations"
            data-ph="select_conversations"
            label={
              <Row gap={1} align="center">
                User conversations
                <Chip
                  size="small"
                  label={conversations.length}
                  sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: 11 } }}
                />
              </Row>
            }
          />
        )}
      </Tabs>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load: {loadError}</Alert>}
      {uploadError && <Alert severity="error" sx={{ mb: 2 }}>Upload failed: {uploadError}</Alert>}
      {!showConversations && tab === "platform" && data?.platform_unavailable_reason === "no_industry" && (
        // Otherwise the tab is just empty, with nothing saying why.
        <Alert severity="info" sx={{ mb: 2 }}>
          Platform documents appear once your company&apos;s industry is set.
        </Alert>
      )}
      {!showConversations && tab === "platform" && data?.platform_unavailable_reason === "disabled" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Platform documents are turned off for your company.
        </Alert>
      )}
      {duplicatePersonalPrompt && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setDuplicatePersonalPrompt(null)}
          data-ph-scope="duplicate"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setDuplicatePromoteOpen(true)}
              data-ph="open_visibility"
            >
              Promote…
            </Button>
          }
        >
          You already have &quot;{duplicatePersonalPrompt.filename}&quot; in your Personal library.
        </Alert>
      )}

      {showConversations ? (
        <ConversationKnowledgeView
          documents={conversations ?? []}
          selectedId={openConversationId}
          onSelect={(id) => setConversationView(true, id)}
          onChanged={() => void refreshConversations()}
          manageableDepts={manageableDepts}
          shareDepts={allDepts.length > 0 ? allDepts : undefined}
        />
      ) : visible.length === 0 && refreshing ? (
        <CardSkeletonList />
      ) : visible.length === 0 ? (
        <div data-ph-scope="empty" style={{ display: "contents" }}>
          <EmptyState
            icon={BookOpen}
            title={
              needsDept && typeof deptId === "number"
                ? `No documents in ${deptName(deptId)} yet`
                : `No ${TIER_LABEL[tab].toLowerCase()} documents yet`
            }
            description="Documents you add appear here and become searchable by the assistant."
            action={
              canUpload ? (
                <Button
                  size="small"
                  startIcon={<Upload size={14} />}
                  onClick={onPickFile}
                  data-ph="upload"
                >
                  Upload one
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <Col gap={1}>
          {visible.map((d) => (
            <DocumentRowItem
              key={d.id}
              doc={d}
              departmentName={docDeptName(d)}
              // The server's per-document write rule (D5), not the tab's
              // upload gate: being able to upload to SOME department says
              // nothing about THIS department's document. Absent -> read-only.
              // Cosmetic (the backend re-enforces every write).
              editable={d.can_write === true}
              // A platform document is every tenant-of-its-industry's
              // knowledge; the server refuses to re-scope or re-share it
              // (409), though a platform admin may re-index or delete it.
              visibilityChangeable={d.can_write === true && tab !== "platform"}
              // Grants are company-member-wide, so sharing only makes sense
              // for company-tier docs. The grants route uses the same write
              // gate, so it also needs this document's can_write.
              shareable={tab === "company" && canManageSharing && d.can_write === true}
              manageableDepts={manageableDepts}
              // Sharing a department/company document reaches any department
              // of the company (decision B), except the one that owns it.
              // Empty (the list call failed) → the dialog falls back to
              // manageableDepts.
              shareDepts={
                allDepts.length > 0
                  ? allDepts.filter((dep) => dep.id !== parseDocDeptId(d.dept_id))
                  : undefined
              }
              onDeleted={refresh}
            />
          ))}
        </Col>
      )}

      {duplicatePersonalPrompt && (
        <PromoteVisibilityDialog
          open={duplicatePromoteOpen}
          onClose={() => setDuplicatePromoteOpen(false)}
          documentId={duplicatePersonalPrompt.documentId}
          documentLabel={duplicatePersonalPrompt.filename}
          // The duplicate is the caller's own Personal copy.
          personal
          manageableDepts={manageableDepts}
          onChanged={() => {
            setDuplicatePersonalPrompt(null);
            refresh();
          }}
        />
      )}
    </Box>
  );
}

function DocumentRowItem({
  doc,
  departmentName,
  editable,
  visibilityChangeable,
  shareable,
  manageableDepts,
  shareDepts,
  onDeleted,
}: {
  doc: DocumentRow;
  /** Resolved name of the document's owning department, or null when the doc
   *  isn't department-scoped. */
  departmentName: string | null;
  /** Delete and add-to-knowledge / re-index. */
  editable: boolean;
  /** The change-visibility (promote to department/company) dialog. */
  visibilityChangeable: boolean;
  shareable: boolean;
  /** Departments the caller may grant documents to — passed through to
   *  PromoteVisibilityDialog's picker. */
  manageableDepts: Department[];
  /** Departments a department/company document may be shared with. */
  shareDepts?: Department[];
  onDeleted: () => void;
}) {
  const { documents } = useAuthedApi();
  const deleteDialogScope = usePortalScope("document-delete_dialog")["data-ph-scope"];
  const [busy, setBusy] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);

  // A web page kept from a chat answer (spec §3.3). Linked only when it is
  // an http(s) address: the value is a page URL a search returned.
  const sourceHref =
    doc.origin_url && /^https?:\/\//i.test(doc.origin_url) ? doc.origin_url : null;
  // A kept page whose fetch failed has no stored bytes, so re-indexing has
  // nothing to read (the server answers 409): it is kept again from the chat.
  const keptPageWithoutBytes = !!doc.origin_url && !doc.source_url;

  const onPromote = async () => {
    if (!documents) return;
    setPromoting(true);
    setError(null);
    try {
      // Re-runs ingestion (chunk + embed + index) on the stored file, moving
      // the doc into the searchable knowledge base. Recovers docs stuck in a
      // non-ready state (extracted / failed) and lets the user re-index ready ones.
      await documents.promote(doc.id);
      onDeleted(); // reuse the list refresh callback
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPromoting(false);
    }
  };

  const onDelete = async () => {
    if (!documents) return;
    setBusy(true);
    setError(null);
    try {
      await documents.remove(doc.id);
      setConfirmOpen(false);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
      throw e; // let ConfirmDialog surface it too
    }
  };

  return (
    <WorkspaceCard data-ph-scope="document">
      <Row align="center" gap={1.5}>
        <FileText size={20} aria-hidden style={{ flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
           
            sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {doc.title || doc.filename}
          </Typography>
          <Row gap={1} align="center" sx={{ mt: 0.25 }}>
            <DocumentStatusChip status={doc.status} />
            {departmentName && (
              <Chip
                size="small"
                variant="outlined"
                icon={<Building2 size={12} />}
                label={departmentName}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {doc.filename} · {formatBytes(doc.byte_size)}
              {doc.chunk_count ? ` · ${doc.chunk_count} chunks` : ""}
            </Typography>
            {sourceHref && (
              <Link
                href={sourceHref}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                aria-label={`Source of ${doc.title || doc.filename}`}
                data-ph="open_source"
              >
                Source
              </Link>
            )}
          </Row>
          {doc.status === "failed" && doc.error && (
            <Typography variant="caption" color="error">
              {doc.error}
              {keptPageWithoutBytes && !/keep it again from the chat/i.test(doc.error)
                ? " Keep it again from the chat."
                : ""}
            </Typography>
          )}
        </Box>
        <Row gap={0.5}>
          {doc.source_url && (
            <Tooltip title="Download">
              <IconButton
                size="small"
                component="a"
                href={`/core/v1/documents/${doc.id}/download`}
                aria-label={`download ${doc.filename}`}
                data-ph="download"
              >
                <Download size={14} />
              </IconButton>
            </Tooltip>
          )}
          {editable && promotable(doc.status) && !keptPageWithoutBytes && (
            <Tooltip
              title={
                doc.status === "ready"
                  ? "Re-index in knowledge base"
                  : "Add to knowledge base"
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={onPromote}
                  disabled={promoting}
                  // Re-indexing a searchable document and adding a stored one
                  // to the knowledge base are different decisions.
                  data-ph={doc.status === "ready" ? "refresh_index" : "create_index"}
                  aria-label={
                    doc.status === "ready"
                      ? `re-index ${doc.filename}`
                      : `promote ${doc.filename} to knowledge base`
                  }
                >
                  {promoting ? <CircularProgress size={14} /> : <DatabaseZap size={14} />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {shareable && (
            <Tooltip title="Share">
              <IconButton
                size="small"
                onClick={() => setShareOpen(true)}
                aria-label={`share ${doc.filename}`}
                data-ph="open_share"
              >
                <Share2 size={14} />
              </IconButton>
            </Tooltip>
          )}
          {visibilityChangeable && (
            <Tooltip title="Change visibility">
              <IconButton
                size="small"
                onClick={() => setVisibilityOpen(true)}
                aria-label={`change visibility of ${doc.filename}`}
                data-ph="open_visibility"
              >
                <Eye size={14} />
              </IconButton>
            </Tooltip>
          )}
          {editable && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={busy}
                aria-label={`delete ${doc.filename}`}
                data-ph="delete"
              >
                <Trash2 size={14} />
              </IconButton>
            </Tooltip>
          )}
        </Row>
      </Row>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      {shareable && (
        <ShareDocumentDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          documentId={doc.id}
          documentLabel={doc.title || doc.filename}
        />
      )}
      {visibilityChangeable && (
        <PromoteVisibilityDialog
          open={visibilityOpen}
          onClose={() => setVisibilityOpen(false)}
          documentId={doc.id}
          documentLabel={doc.title || doc.filename}
          // Personal by the ROW (user_id set), as the backend decides — not
          // by the tab: a legacy department grant can list a personal doc
          // under Department.
          personal={doc.user_id != null}
          manageableDepts={manageableDepts}
          shareDepts={shareDepts}
          // Reuse the existing "refetch the list" callback rather than adding
          // a parallel onChanged prop — onDeleted is already a generic
          // refresh trigger despite its name (onPromote above reuses it the
          // same way).
          onChanged={onDeleted}
        />
      )}
      <ConfirmDialog
        open={confirmOpen}
        title="Delete this document?"
        body={<><strong>{doc.filename}</strong> will be removed from your knowledge base.</>}
        confirmLabel="Delete"
        destructive
        onConfirm={onDelete}
        onClose={() => setConfirmOpen(false)}
        scope={deleteDialogScope}
      />
    </WorkspaceCard>
  );
}
